import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useSession } from '@/lib/auth-client';
import { trpcClient } from '@/lib/trpc';

interface AdminModeContextType {
  isAdminMode: boolean;
  impersonatedOrgId: string | null;
  impersonatedOrgName: string | null;
  enterAdminMode: (orgId: string, orgName: string) => void;
  exitAdminMode: () => void;
  canUseAdminMode: boolean;
}

const AdminModeContext = createContext<AdminModeContextType | undefined>(undefined);

const ADMIN_MODE_STORAGE_KEY = 'deepguide_admin_mode';

interface AdminModeData {
  orgId: string;
  orgName: string;
  timestamp: number;
}

export function AdminModeProvider({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [impersonatedOrgId, setImpersonatedOrgId] = useState<string | null>(null);
  const [impersonatedOrgName, setImpersonatedOrgName] = useState<string | null>(null);

  // Capture URL params ONCE on mount before any routing
  const urlParamsRef = useRef<{ orgId: string; orgName: string } | null>(null);
  if (urlParamsRef.current === null) {
    const params = new URLSearchParams(window.location.search);
    const adminModeParam = params.get('admin_mode');
    const orgIdParam = params.get('org_id');
    const orgNameParam = params.get('org_name');

    if (adminModeParam === 'true' && orgIdParam && orgNameParam) {
      urlParamsRef.current = {
        orgId: orgIdParam,
        orgName: decodeURIComponent(orgNameParam),
      };
      console.log('[AdminMode] 🎯 Captured URL params on mount:', urlParamsRef.current);
    }
  }

  // Check if user is SITE_ADMIN
  const canUseAdminMode = session?.user?.role === 'SITE_ADMIN';

  console.log('[AdminMode] Provider render:', {
    isPending,
    hasSession: !!session,
    userRole: session?.user?.role,
    canUseAdminMode,
    isAdminMode,
    capturedParams: urlParamsRef.current,
    storedInLocal: localStorage.getItem(ADMIN_MODE_STORAGE_KEY),
  });

  // Load admin mode from storage on mount
  useEffect(() => {
    console.log('[AdminMode] Loading admin mode from storage, canUseAdminMode:', canUseAdminMode, 'isPending:', isPending);

    // Wait for session to load before checking permissions
    if (isPending) {
      console.log('[AdminMode] Session still loading, not checking storage yet');
      return;
    }

    if (!canUseAdminMode) {
      // Clear admin mode if user is not SITE_ADMIN (only after session loads)
      console.log('[AdminMode] User is not SITE_ADMIN, clearing admin mode');
      localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      return;
    }

    const stored = localStorage.getItem(ADMIN_MODE_STORAGE_KEY);
    console.log('[AdminMode] Stored admin mode data:', stored);

    if (stored) {
      try {
        const data: AdminModeData = JSON.parse(stored);
        console.log('[AdminMode] Parsed admin mode data:', data);

        // Check if admin mode session is still valid (24 hours)
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        const age = Date.now() - data.timestamp;
        console.log('[AdminMode] Age:', age, 'ms, maxAge:', maxAge, 'ms');

        if (age < maxAge) {
          setIsAdminMode(true);
          setImpersonatedOrgId(data.orgId);
          setImpersonatedOrgName(data.orgName);
          console.log('[AdminMode] ✓ Restored admin mode session:', data.orgName);
        } else {
          // Expired - clear it
          localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
          console.log('[AdminMode] Admin mode session expired');
        }
      } catch (error) {
        console.error('[AdminMode] Failed to parse stored admin mode:', error);
        localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      }
    } else {
      console.log('[AdminMode] No stored admin mode data found');
    }
  }, [canUseAdminMode, isPending]);

  // Define logAdminAccess first so it can be used in enterAdminMode
  const logAdminAccess = useCallback(async (orgId: string, orgName: string, action: 'enter' | 'exit') => {
    try {
      // Use tRPC instead of fetch to avoid proxy issues
      await trpcClient.admin.logAdminAccess.mutate({
        action: `admin_mode_${action}`,
        resourceType: 'organization',
        resourceId: orgId,
        metadata: {
          organizationName: orgName,
          userId: session?.user?.id,
          userEmail: session?.user?.email,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        },
      });
    } catch (error) {
      // Silently fail - audit logging is non-critical
      // Only log in development mode
      if (import.meta.env.DEV) {
        console.warn('[AdminMode] Audit log failed (non-critical):', error instanceof Error ? error.message : 'Unknown error');
      }
    }
  }, [session]);

  const enterAdminMode = useCallback(async (orgId: string, orgName: string) => {
    console.log('[AdminMode] enterAdminMode called with:', { orgId, orgName, canUseAdminMode });

    if (!canUseAdminMode) {
      console.warn('[AdminMode] User is not SITE_ADMIN, cannot enter admin mode');
      return;
    }

    try {
      // Store admin mode UI state in localStorage
      const data: AdminModeData = {
        orgId,
        orgName,
        timestamp: Date.now(),
      };

      console.log('[AdminMode] Storing admin mode data in localStorage:', data);
      localStorage.setItem(ADMIN_MODE_STORAGE_KEY, JSON.stringify(data));

      setIsAdminMode(true);
      setImpersonatedOrgId(orgId);
      setImpersonatedOrgName(orgName);

      // Log admin access for audit trail
      await logAdminAccess(orgId, orgName, 'enter');

      // Set the target organization as the SITE_ADMIN's active org
      // This makes the SITE_ADMIN "become" a user of that org
      console.log('[AdminMode] Setting active organization to target org...');
      await trpcClient.organizations.setActiveOrganization.mutate({
        organizationId: orgId
      });
      console.log('[AdminMode] ✓ Active organization set successfully');

      // Fetch the organization's workspaces
      console.log('[AdminMode] Fetching org workspaces...');
      const result = await trpcClient.organizations.getOrganizationWithWorkspaces.query({ id: orgId });
      console.log('[AdminMode] Organization data:', result);

      if (result.organization?.workspaces?.[0]) {
        const firstWorkspace = result.organization.workspaces[0];
        console.log('[AdminMode] Navigating to org first workspace:', firstWorkspace.id);
        window.location.href = `/workspaces/${firstWorkspace.id}`;
      } else {
        // No workspaces, go to home
        console.log('[AdminMode] No workspaces found, navigating to home');
        window.location.href = '/';
      }
    } catch (error) {
      console.error('[AdminMode] Failed to enter admin mode:', error);
      // Clear localStorage on failure
      localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
    }
  }, [canUseAdminMode, logAdminAccess]);

  // Check for admin mode params captured on mount
  useEffect(() => {
    console.log('[AdminMode] Checking for captured admin mode params', {
      isPending,
      canUseAdminMode,
      hasSession: !!session,
      isAdminMode,
      capturedParams: urlParamsRef.current,
    });

    // Wait for session to load
    if (isPending) {
      console.log('[AdminMode] Session still loading, waiting...');
      return;
    }

    if (!canUseAdminMode) {
      console.log('[AdminMode] User cannot use admin mode');
      // Clear captured params if user is not SITE_ADMIN
      urlParamsRef.current = null;
      return;
    }

    // Don't process URL params if already in admin mode
    if (isAdminMode) {
      console.log('[AdminMode] Already in admin mode, skipping param check');
      return;
    }

    // Check captured params from mount
    if (urlParamsRef.current) {
      console.log('[AdminMode] ✓ Valid admin mode params found, entering admin mode', urlParamsRef.current);

      // Enter admin mode
      enterAdminMode(urlParamsRef.current.orgId, urlParamsRef.current.orgName);

      // Clear the ref so we don't process again
      urlParamsRef.current = null;
    } else {
      console.log('[AdminMode] No captured admin mode params');
    }
  }, [isPending, canUseAdminMode, isAdminMode, enterAdminMode]);

  const exitAdminMode = async () => {
    try {
      // Log exit for audit trail
      if (impersonatedOrgId && impersonatedOrgName) {
        await logAdminAccess(impersonatedOrgId, impersonatedOrgName, 'exit');
      }

      // Clear all admin mode storage
      console.log('[AdminMode] Clearing all admin mode storage');
      localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      localStorage.removeItem('partner:delegation'); // Also clear partner delegation if present
      localStorage.removeItem('auth:activeOrg');
      localStorage.removeItem('auth:activeWorkspace');
      localStorage.removeItem('auth:activeWorkspaceId');

      setIsAdminMode(false);
      setImpersonatedOrgId(null);
      setImpersonatedOrgName(null);

      console.log('[AdminMode] Exited admin mode, navigating to admin panel');

      // Navigate to admin panel
      window.location.href = import.meta.env.VITE_ADMIN_URL || 'http://localhost:5174';
    } catch (error) {
      console.error('[AdminMode] Failed to exit admin mode:', error);
      // Still clear storage and navigate on error
      localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      localStorage.removeItem('partner:delegation');
      localStorage.removeItem('auth:activeOrg');
      localStorage.removeItem('auth:activeWorkspace');
      localStorage.removeItem('auth:activeWorkspaceId');
      window.location.href = import.meta.env.VITE_ADMIN_URL || 'http://localhost:5174';
    }
  };

  const value: AdminModeContextType = {
    isAdminMode,
    impersonatedOrgId,
    impersonatedOrgName,
    enterAdminMode,
    exitAdminMode,
    canUseAdminMode,
  };

  return (
    <AdminModeContext.Provider value={value}>
      {children}
    </AdminModeContext.Provider>
  );
}

export function useAdminMode() {
  const context = useContext(AdminModeContext);
  if (context === undefined) {
    throw new Error('useAdminMode must be used within AdminModeProvider');
  }
  return context;
}
